import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import VendorEditModal, { type VendorEditData } from '../../guest/vendor-detail/modals/vendorEditModal';
import type { VendorBasicInfo } from '../../guest/vendor-detail/types';

export default function WeddingHallMyVendorPage() {
  const [myInfo, setMyInfo] = useState<VendorBasicInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchMyInfo = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status) {
          setMyInfo(result.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch my info:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyInfo();
  }, [fetchMyInfo]);

  const handleUpdateMyVendor = async (data: VendorEditData) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor/me', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        alert('업체 정보가 수정되었습니다.');
        setShowEditModal(false);
        fetchMyInfo();
      } else {
        const err = await response.json();
        alert(`수정 실패: ${err.message}`);
      }
    } catch (error) {
      console.error('Update Error:', error);
      alert('오류가 발생했습니다.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8 overflow-y-auto">

        {/* 헤더 */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">내 업체 정보</h1>
            <p className="text-sm text-gray-600 mt-1">웨딩홀 업체 정보를 확인하고 관리합니다</p>
          </div>
          <button
            onClick={() => setShowEditModal(true)}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-edit-line"></i> 정보 수정
          </button>
        </div>

        {/* 정보 카드 */}
        {myInfo ? (
          <div className="space-y-6 max-w-5xl">
            {/* 1. 기본 정보 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">기본 정보</h3>
              <div className="grid grid-cols-2 gap-6">
                <InfoItem label="업체명" value={myInfo.name} />
                <InfoItem label="사업자번호" value={myInfo.businessNumber} />
                <InfoItem label="대표자/담당자" value={myInfo.managerName} />
                <InfoItem label="연락처" value={myInfo.contact} />
                <InfoItem label="이메일" value={myInfo.email} />
                <InfoItem label="상태" value={
                  <span className={`px-2 py-1 rounded text-xs font-medium ${myInfo.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {myInfo.status}
                  </span>
                } />
                <div className="col-span-2">
                  <InfoItem label="주소" value={myInfo.address} />
                </div>
              </div>
            </div>

            {/* 2. 정산 정보 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">정산 정보</h3>
              <div className="grid grid-cols-2 gap-6">
                <InfoItem label="은행명" value={myInfo.bankName} />
                <InfoItem label="계좌번호" value={myInfo.accountNumber} />
                <InfoItem label="예금주" value={myInfo.accountHolder} />
                <InfoItem label="수수료율" value={`${myInfo.commissionRate}%`} />
              </div>
            </div>

            {/* 3. 메모 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">메모</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{myInfo.memo || '등록된 메모가 없습니다.'}</p>
            </div>
          </div>
        ) : (
          <div className="text-center py-20 text-gray-500">정보를 불러올 수 없습니다.</div>
        )}

        {/* 수정 모달 */}
        <VendorEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          initialData={myInfo}
          onSave={handleUpdateMyVendor}
        />
      </div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string, value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500 mb-1">{label}</dt>
      <dd className="text-base text-gray-900 font-medium">{value || '-'}</dd>
    </div>
  );
}
